import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FolderKanban, UserCheck, Calculator, Settings, History, LayoutDashboard, TrendingUp, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import UserManagement from './components/UserManagement';
import ProjectManagement from './components/ProjectManagement';
import AdminHeader from './components/AdminHeader';
import ClientsList from '../clients-list';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const { lastUpdate, subscribeToProjects, subscribeToUsers, unsubscribe } = useRealtime();
  const [activeTab, setActiveTab] = useState('projects');
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        setLoading(false);
      }
    }
  }, [user, profile, authLoading, navigate]);

  // Subscribe to real-time updates
  useEffect(() => {
    const projectsChannel = subscribeToProjects();
    const usersChannel = subscribeToUsers();
    
    return () => {
      unsubscribe(projectsChannel);
      unsubscribe(usersChannel);
    };
  }, [subscribeToProjects, subscribeToUsers, unsubscribe]);

  // Trigger refresh when real-time updates detected
  useEffect(() => {
    if (lastUpdate?.projects || lastUpdate?.users) {
      setRefreshKey(prev => prev + 1);
    }
  }, [lastUpdate?.projects, lastUpdate?.users]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader userName={profile?.fullName || profile?.email} onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Painel Administrativo</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            Gerencie usuários, projetos e cronogramas do sistema CTOBRA
          </p>
        </div>

        <div className="flex gap-2 sm:gap-4 mb-4 sm:mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <LayoutDashboard className="h-4 w-4 sm:h-5 sm:w-5" />
            Dashboard
          </button>
          <button
            onClick={() => navigate('/reports-and-analytics-dashboard')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
            Relatórios
          </button>
          <button
            onClick={() => navigate('/project-templates-management')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'projects' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <FolderKanban className="h-4 w-4 sm:h-5 sm:w-5" />
            Projetos
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'users' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="h-4 w-4 sm:h-5 sm:w-5" />
            Usuários
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'clients' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <UserCheck className="h-4 w-4 sm:h-5 sm:w-5" />
            Clientes
          </button>
          <button
            onClick={() => navigate('/profit-calculator')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <Calculator className="h-4 w-4 sm:h-5 sm:w-5" />
            Calculadora
          </button>
          <button
            onClick={() => navigate('/profit-calculation-history')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <History className="h-4 w-4 sm:h-5 sm:w-5" />
            Histórico
          </button>
          <button
            onClick={() => navigate('/tax-configuration-settings')}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
            Impostos
          </button>
        </div>

        <div className="mt-6">
          {activeTab === 'projects' && <ProjectManagement key={`projects-${refreshKey}`} />}
          {activeTab === 'users' && <UserManagement key={`users-${refreshKey}`} />}
          {activeTab === 'clients' && <ClientsList key={`clients-${refreshKey}`} />}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;